import { match } from 'ts-pattern'

import { customLocales } from '@sequencehq/utils/dates'
import { formatDistanceToNow } from '@sequencehq/utils/dates'

import { CounterSigner, QuoteContact } from 'Cube/domain/cube.domain.types'
import { OnlineStatus, UniqueViewer } from '../../types'
import { ONLINE_THRESHOLD } from './constants'

const getMsSinceLastViewed = (
  lastViewedAt: string | undefined
): number | undefined => {
  if (!lastViewedAt) return undefined
  return new Date().getTime() - new Date(lastViewedAt).getTime()
}

export const getStatus = (
  msSinceLastViewed: number | undefined
): OnlineStatus => {
  if (typeof msSinceLastViewed === 'undefined') return 'OFFLINE'
  return msSinceLastViewed < ONLINE_THRESHOLD ? 'ONLINE' : 'OFFLINE'
}

export const getCaption = ({
  status,
  lastViewedAt
}: {
  status: OnlineStatus
  lastViewedAt: string | undefined
}): string => {
  return match(status)
    .with('ONLINE', () => 'Online')
    .with('OFFLINE', () => {
      if (!lastViewedAt) return 'Not seen'

      return `Last seen ${formatDistanceToNow(lastViewedAt, {
        addSuffix: true,
        locale: customLocales.enGb
      })}`
    })
    .exhaustive()
}

export const formatLastSeen = ({
  signers,
  uniqueViewersData
}: {
  signers: Array<QuoteContact | CounterSigner>
  uniqueViewersData: UniqueViewer[]
}) => {
  return (
    signers
      .map(signer => {
        const viewerDataForSigner = uniqueViewersData.find(
          viewerData => viewerData.email === signer.email
        )

        const msSinceLastViewed = getMsSinceLastViewed(
          viewerDataForSigner?.lastViewedAt
        )
        const status = getStatus(msSinceLastViewed)
        const caption = getCaption({
          status,
          lastViewedAt: viewerDataForSigner?.lastViewedAt
        })

        return {
          name: signer.name,
          email: signer.email,
          status,
          caption,
          msSinceLastViewed
        }
      })
      /**
       * Sort by last seen, with contacts with no data appearing last
       */
      .sort((a, b) => {
        if (a.msSinceLastViewed === undefined) return 1
        if (b.msSinceLastViewed === undefined) return -1
        return a.msSinceLastViewed - b.msSinceLastViewed
      })
  )
}
